import { OnboardingStage, type WaitingOn } from '@/types';

/**
 * Presentation only.
 *
 * Which stage a guide is in is decided on the server and shipped per row — see
 * `ONBOARDING_STAGE_RULES` in
 * server/src/modules/guide-profile/guide-lifecycle.state-machine.ts. Nothing
 * here branches on lifecycle fields, so the tab contents and the counts cannot
 * drift apart.
 */

export interface OnboardingTab {
  stage: OnboardingStage;
  label: string;
  description: string;
}

/** Tab order. LIVE and SUSPENDED are not onboarding work — see All Guides. */
export const ONBOARDING_TABS: readonly OnboardingTab[] = [
  {
    stage: OnboardingStage.BACKGROUND_CHECK,
    label: 'Background Check',
    description:
      'Applicants whose details need sending to SpringVerify, and those already with them awaiting a verdict.',
  },
  {
    stage: OnboardingStage.AWAITING_GUIDE,
    label: 'Awaiting Guide',
    description:
      'Verified applicants who have not finished and submitted their profile yet. Nothing to do here but chase.',
  },
  {
    stage: OnboardingStage.PROFILE_REVIEW,
    label: 'Profile Review',
    description: 'Profiles submitted and waiting on your decision.',
  },
  {
    stage: OnboardingStage.READY_TO_PUBLISH,
    label: 'Ready to Publish',
    description:
      'Approved guides who are not bookable yet because they have not set their pricing.',
  },
  {
    stage: OnboardingStage.REJECTED,
    label: 'Rejected',
    description:
      'Applicants whose background check failed. You can re-open verification to grant a retry.',
  },
];

export const STAGE_LABEL: Record<OnboardingStage, string> = {
  [OnboardingStage.BACKGROUND_CHECK]: 'Background check',
  [OnboardingStage.AWAITING_GUIDE]: 'Awaiting guide',
  [OnboardingStage.PROFILE_REVIEW]: 'Profile review',
  [OnboardingStage.READY_TO_PUBLISH]: 'Ready to publish',
  [OnboardingStage.REJECTED]: 'Rejected',
  [OnboardingStage.SUSPENDED]: 'Suspended',
  [OnboardingStage.LIVE]: 'Live',
};

export const WAITING_ON_LABEL: Record<WaitingOn, string> = {
  admin: 'You',
  springverify: 'SpringVerify',
  guide: 'Guide',
  none: '—',
};

/** Stages the admin can move right now. Drives the sidebar badge. */
export const ADMIN_ACTIONABLE_STAGES: readonly OnboardingStage[] = [
  OnboardingStage.BACKGROUND_CHECK,
  OnboardingStage.PROFILE_REVIEW,
];

export function isOnboardingStage(value: string | null): value is OnboardingStage {
  return ONBOARDING_TABS.some((tab) => tab.stage === value);
}

/**
 * Why a NOT_STARTED row cannot be actioned.
 *
 * The state machine only lets an applicant or the system move NOT_STARTED to
 * PENDING, so an admin pressing anything on such a row would get a 409.
 */
export const LEGACY_RECORD_REASON =
  'Legacy record with no application on file — the applicant has to re-apply before verification can start.';

/** Whole days since `iso`, or null when there is no date. */
export function daysSince(iso?: string): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** "6 days", for the waiting-since column. Undefined dates render as a dash. */
export function formatWaitingSince(iso?: string): string {
  const days = daysSince(iso);
  if (days === null) return '—';
  if (days < 1) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

/** "Sent 6 days ago", for the background-check column. Shares `daysSince` with
 * `formatWaitingSince` so the two can never disagree by a day. */
export function formatSentOn(iso?: string): string {
  const days = daysSince(iso);
  if (days === null) return '—';
  if (days < 1) return 'Sent today';
  if (days === 1) return 'Sent 1 day ago';
  return `Sent ${days} days ago`;
}
